import React, { useRef } from 'react';
import { StyleSheet, TouchableOpacity, View, type TouchableOpacityProps } from 'react-native';

import { theme } from '@garnish/constants';

import { useHovered } from '../../../../../../hooks';
import { webOnlyStyles } from '../../../../../../utils';

// ─────────────────────────────────────────────────────────────────────────────

export const NavbarFloatingLogoPressable = (props: Props) => {
  const { children, disabled, ...restProps } = props;

  // ───────────────────────────────────────────────────────────────────────────

  if (disabled) {
    return <View style={styles.container}>{children}</View>;
  }

  return <LogoPressableContainer {...restProps}>{children}</LogoPressableContainer>;
};

// ─────────────────────────────────────────────────────────────────────────────

const LogoPressableContainer = (props: Props) => {
  const { children, disabled, onPress, 'aria-label': ariaLabel, ...restProps } = props;

  // ─── Refs ──────────────────────────────────────────────────────────────────

  const pressableRef = useRef(null);
  const isHovered = useHovered(pressableRef);

  // ─── Styles ────────────────────────────────────────────────────────────────

  const containerStyles = [
    styles.container,
    containerWebStyles,
    !disabled && isHovered ? styles.containerHovered : undefined,
  ];

  // ───────────────────────────────────────────────────────────────────────────

  return (
    <TouchableOpacity
      {...restProps}
      aria-label={ariaLabel}
      role="link"
      disabled={disabled}
      activeOpacity={0.7}
      style={containerStyles}
      onPress={onPress}
    >
      <View ref={pressableRef}>{children}</View>
    </TouchableOpacity>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flexShrink: 0,
  },
  containerHovered: {
    opacity: 0.8,
  },
});

const containerWebStyles = webOnlyStyles({
  transition: `opacity ${theme.transitions.base}ms`,
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = Pick<TouchableOpacityProps, 'disabled' | 'onPress' | 'aria-label' | 'children'>;
